/**
 * WorldMapData.ts - FF1-style world map data
 * Contains terrain layouts, locations, and encounter zones for the overworld
 */

// ========== TERRAIN TYPES ==========

export enum TerrainType {
  OCEAN = 0,
  GRASS = 1,
  FOREST = 2,
  MOUNTAIN = 3,
  DESERT = 4,
  SWAMP = 5,
  RIVER = 6,
  ROAD = 7,
  BRIDGE = 8,
  TOWN = 9,
  CASTLE = 10,
  CAVE = 11,
  SHRINE = 12,
}

// ========== LOCATION TYPES ==========

export enum LocationType {
  TOWN = 'TOWN',
  CASTLE = 'CASTLE',
  CAVE = 'CAVE',
  SHRINE = 'SHRINE',
  DUNGEON = 'DUNGEON',
  SPECIAL = 'SPECIAL',
}

export interface Location {
  id: string;
  name: string;
  type: LocationType;
  x: number;
  y: number;
  description: string;
  requiredItem?: string; // Item needed to enter (e.g., "MYSTIC_KEY")
  bossId?: number; // Enemy ID from EnemyData.sol
  encounterZone?: string; // Which encounter table to use
  shops?: {
    weapons?: boolean;
    armor?: boolean;
    items?: boolean;
    magic?: boolean;
    inn?: boolean;
  };
}

// ========== ENCOUNTER ZONES ==========

export interface EncounterZone {
  id: string;
  name: string;
  minLevel: number;
  maxLevel: number;
  encounterRate: number; // Base rate 0-100
  enemyGroups: number[][]; // Arrays of enemy IDs from EnemyData.sol
  weights: number[]; // Relative probability of each group
}

// ========== WORLD MAP DATA ==========

// Map size: 64x64 tiles (like FF1)
export const WORLD_MAP_WIDTH = 64;
export const WORLD_MAP_HEIGHT = 64;

// Helper function to safely set a terrain tile
function setTerrain(map: TerrainType[][], y: number, x: number, terrain: TerrainType): void {
  const row = map[y];
  if (row) {
    row[x] = terrain;
  }
}

// Helper function to create the initial world map
function createWorldMap(): TerrainType[][] {
  // Initialize with ocean
  const map: TerrainType[][] = Array(WORLD_MAP_HEIGHT)
    .fill(null)
    .map(() => Array(WORLD_MAP_WIDTH).fill(TerrainType.OCEAN));

  // ========== MAIN CONTINENT (CENTER-WEST) ==========
  // Cornelia region (northwest - starting area)
  fillRect(map, 8, 8, 18, 16, TerrainType.GRASS);

  // Cornelia Castle and Town
  setTerrain(map, 10, 10, TerrainType.CASTLE);
  setTerrain(map, 10, 12, TerrainType.TOWN);

  // Temple of Fiends (north of Cornelia)
  setTerrain(map, 9, 11, TerrainType.CAVE);

  // Road from Cornelia south
  for (let y = 13; y <= 18; y++) {
    setTerrain(map, y, 11, TerrainType.ROAD);
  }

  // Forest west of Cornelia
  fillRect(map, 11, 8, 3, 6, TerrainType.FOREST);

  // Pravoka region (coast town - south of Cornelia)
  fillRect(map, 19, 9, 6, 8, TerrainType.GRASS);
  setTerrain(map, 20, 12, TerrainType.TOWN); // Pravoka
  setTerrain(map, 21, 11, TerrainType.BRIDGE); // Bridge over river

  // River flowing east from Pravoka
  for (let x = 13; x <= 20; x++) {
    setTerrain(map, 21, x, TerrainType.RIVER);
  }

  // Marsh Cave region (southeast swampland)
  fillRect(map, 24, 16, 8, 10, TerrainType.SWAMP);
  setTerrain(map, 26, 19, TerrainType.CAVE); // Marsh Cave

  // Elfheim region (central east forest)
  fillRect(map, 18, 24, 12, 14, TerrainType.GRASS);
  fillRect(map, 19, 25, 8, 10, TerrainType.FOREST);
  setTerrain(map, 23, 29, TerrainType.TOWN); // Elfheim
  setTerrain(map, 22, 27, TerrainType.CASTLE); // Elven Castle

  // Western Keep (isolated western peninsula)
  fillRect(map, 14, 3, 8, 5, TerrainType.GRASS);
  setTerrain(map, 17, 5, TerrainType.CASTLE); // Western Keep

  // Mountain range (central barrier)
  for (let y = 8; y <= 35; y++) {
    setTerrain(map, y, 21, TerrainType.MOUNTAIN);
    if (y >= 15 && y <= 25) {
      setTerrain(map, y, 22, TerrainType.MOUNTAIN);
    }
  }
  // Mountain pass
  setTerrain(map, 18, 21, TerrainType.GRASS);
  setTerrain(map, 18, 22, TerrainType.GRASS);

  // Melmond region (southwest - troubled town)
  fillRect(map, 32, 8, 10, 12, TerrainType.GRASS);
  setTerrain(map, 36, 13, TerrainType.TOWN); // Melmond
  fillRect(map, 38, 10, 4, 6, TerrainType.FOREST);

  // Earth Cave (near Melmond - Lich's lair)
  setTerrain(map, 40, 11, TerrainType.CAVE); // Earth Cave entrance

  // Titan's Tunnel (passage under mountains)
  setTerrain(map, 34, 21, TerrainType.CAVE); // West entrance
  setTerrain(map, 34, 23, TerrainType.CAVE); // East entrance

  // Desert region (far south)
  fillRect(map, 44, 10, 12, 20, TerrainType.DESERT);

  // Crescent Lake (sage town in desert)
  fillRect(map, 48, 18, 4, 8, TerrainType.GRASS);
  for (let x = 20; x <= 24; x++) {
    setTerrain(map, 50, x, TerrainType.RIVER); // Crescent shaped lake
  }
  setTerrain(map, 49, 22, TerrainType.RIVER);
  setTerrain(map, 51, 22, TerrainType.RIVER);
  setTerrain(map, 50, 25, TerrainType.TOWN); // Crescent Lake town

  // Gurgu Volcano region (far southwest - Kary's lair)
  fillRect(map, 50, 4, 8, 8, TerrainType.MOUNTAIN);
  setTerrain(map, 54, 7, TerrainType.CAVE); // Volcano entrance

  // ========== EASTERN CONTINENT ==========
  // Gaia region (far northeast)
  fillRect(map, 6, 44, 12, 14, TerrainType.GRASS);
  setTerrain(map, 10, 50, TerrainType.TOWN); // Gaia
  fillRect(map, 8, 46, 6, 8, TerrainType.FOREST);

  // Ice Cave region (frozen north)
  fillRect(map, 4, 48, 8, 8, TerrainType.MOUNTAIN);
  setTerrain(map, 7, 52, TerrainType.CAVE); // Ice Cave

  // Onrac region (eastern coast)
  fillRect(map, 28, 50, 10, 10, TerrainType.GRASS);
  setTerrain(map, 32, 54, TerrainType.TOWN); // Onrac
  setTerrain(map, 34, 55, TerrainType.SHRINE); // Sea Shrine entrance

  // ========== SOUTHERN ISLANDS ==========
  // Lufenia (ancient civilization - south island)
  fillRect(map, 54, 40, 8, 10, TerrainType.GRASS);
  setTerrain(map, 57, 45, TerrainType.TOWN); // Lufenia
  setTerrain(map, 56, 43, TerrainType.SHRINE); // Mirage Tower
  fillRect(map, 55, 42, 4, 5, TerrainType.DESERT);

  // Small islands with caves and treasures
  fillRect(map, 40, 38, 3, 4, TerrainType.GRASS);
  setTerrain(map, 41, 40, TerrainType.CAVE); // Treasure cave

  fillRect(map, 22, 54, 4, 4, TerrainType.GRASS);
  setTerrain(map, 24, 56, TerrainType.CAVE); // Hidden cave

  // Waterfall cave (central-east)
  fillRect(map, 30, 42, 3, 3, TerrainType.GRASS);
  setTerrain(map, 31, 43, TerrainType.CAVE); // Waterfall

  // ========== CARDIA ISLANDS (NORTHEAST CHAIN) ==========
  // Small island chain in far northeast
  fillRect(map, 8, 58, 3, 3, TerrainType.GRASS);
  fillRect(map, 12, 60, 2, 2, TerrainType.GRASS);

  return map;
}

// Helper function to fill rectangular region
function fillRect(
  map: TerrainType[][],
  startY: number,
  startX: number,
  height: number,
  width: number,
  terrain: TerrainType
): void {
  for (let y = startY; y < startY + height && y < WORLD_MAP_HEIGHT; y++) {
    for (let x = startX; x < startX + width && x < WORLD_MAP_WIDTH; x++) {
      if (y >= 0 && x >= 0) {
        setTerrain(map, y, x, terrain);
      }
    }
  }
}

// Export the world map
export const WORLD_MAP: TerrainType[][] = createWorldMap();

// ========== LOCATIONS ==========

export const LOCATIONS: Location[] = [
  // ===== STARTING AREA =====
  {
    id: 'CORNELIA_CASTLE',
    name: 'Cornelia Castle',
    type: LocationType.CASTLE,
    x: 10,
    y: 10,
    description: 'The royal castle where Princess Sarah was held captive by Garland.',
    shops: { items: true, inn: true },
  },
  {
    id: 'CORNELIA',
    name: 'Cornelia',
    type: LocationType.TOWN,
    x: 12,
    y: 10,
    description: 'The peaceful starting town. Buy your first equipment here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'TEMPLE_OF_FIENDS',
    name: 'Temple of Fiends',
    type: LocationType.DUNGEON,
    x: 11,
    y: 9,
    description: 'An ancient temple corrupted by Garland. Your first trial.',
    bossId: 43, // Chaos (initial form - tutorial boss)
    encounterZone: 'TEMPLE_FIENDS',
  },

  // ===== WESTERN REGION =====
  {
    id: 'WESTERN_KEEP',
    name: 'Western Keep',
    type: LocationType.CASTLE,
    x: 5,
    y: 17,
    description: 'An isolated keep on the western peninsula.',
    shops: { items: true },
  },

  // ===== COASTAL REGION =====
  {
    id: 'PRAVOKA',
    name: 'Pravoka',
    type: LocationType.TOWN,
    x: 12,
    y: 20,
    description: 'A port town once terrorized by pirates. Gain your ship here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'MARSH_CAVE',
    name: 'Marsh Cave',
    type: LocationType.CAVE,
    x: 19,
    y: 26,
    description: 'A treacherous cave hidden in the swamps. Find the Crown here.',
    encounterZone: 'MARSH',
  },

  // ===== ELFHEIM REGION =====
  {
    id: 'ELFHEIM',
    name: 'Elfheim',
    type: LocationType.TOWN,
    x: 29,
    y: 23,
    description: 'The woodland home of the elves. High-level magic sold here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'ELVEN_CASTLE',
    name: 'Elven Castle',
    type: LocationType.CASTLE,
    x: 27,
    y: 22,
    description: 'The castle of the Elven Prince, cursed to eternal sleep.',
    requiredItem: 'MYSTIC_KEY',
  },

  // ===== MELMOND REGION =====
  {
    id: 'MELMOND',
    name: 'Melmond',
    type: LocationType.TOWN,
    x: 13,
    y: 36,
    description: 'A town suffering from the earth rotting beneath it.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'EARTH_CAVE',
    name: 'Earth Cave',
    type: LocationType.DUNGEON,
    x: 11,
    y: 40,
    description: 'The lair of Lich, the Earth Fiend. Restore the Earth Crystal.',
    bossId: 39, // Lich
    encounterZone: 'EARTH_CAVE',
    requiredItem: 'STAR_RUBY',
  },
  {
    id: 'TITANS_TUNNEL',
    name: "Titan's Tunnel",
    type: LocationType.CAVE,
    x: 21,
    y: 34,
    description: 'A tunnel through the mountains, blocked by a sleeping titan.',
    requiredItem: 'RUBY',
  },

  // ===== DESERT REGION =====
  {
    id: 'CRESCENT_LAKE',
    name: 'Crescent Lake',
    type: LocationType.TOWN,
    x: 25,
    y: 50,
    description: 'A town of wise sages who know the locations of the Orbs.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },

  // ===== VOLCANO REGION =====
  {
    id: 'GURGU_VOLCANO',
    name: 'Gurgu Volcano',
    type: LocationType.DUNGEON,
    x: 7,
    y: 54,
    description: 'A fiery volcano where Kary, the Fire Fiend, resides.',
    bossId: 40, // Kary
    encounterZone: 'VOLCANO',
    requiredItem: 'LEVISTONE',
  },

  // ===== NORTHERN REGION =====
  {
    id: 'GAIA',
    name: 'Gaia',
    type: LocationType.TOWN,
    x: 50,
    y: 10,
    description: 'A remote town in the far north.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      inn: true,
    },
  },
  {
    id: 'ICE_CAVE',
    name: 'Ice Cave',
    type: LocationType.CAVE,
    x: 52,
    y: 7,
    description: 'A frozen cavern. Find the Levistone to access the airship.',
    encounterZone: 'ICE_CAVE',
  },

  // ===== EASTERN COAST =====
  {
    id: 'ONRAC',
    name: 'Onrac',
    type: LocationType.TOWN,
    x: 54,
    y: 32,
    description: 'A seaside resort town. Gateway to the underwater shrine.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'SEA_SHRINE',
    name: 'Sea Shrine',
    type: LocationType.SHRINE,
    x: 55,
    y: 34,
    description: 'An underwater temple where Kraken, the Water Fiend, dwells.',
    bossId: 41, // Kraken
    encounterZone: 'SEA_SHRINE',
    requiredItem: 'OXYALE',
  },

  // ===== SOUTHERN ISLANDS =====
  {
    id: 'LUFENIA',
    name: 'Lufenia',
    type: LocationType.TOWN,
    x: 45,
    y: 57,
    description: 'An ancient town of a lost civilization. Most powerful equipment.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'MIRAGE_TOWER',
    name: 'Mirage Tower',
    type: LocationType.SHRINE,
    x: 43,
    y: 56,
    description: 'A mysterious tower that appears in the desert.',
    encounterZone: 'MIRAGE_TOWER',
    requiredItem: 'CHIME',
  },
  {
    id: 'SKY_CASTLE',
    name: 'Sky Castle',
    type: LocationType.DUNGEON,
    x: 43,
    y: 56, // Same location as Mirage Tower (floats above)
    description: 'The flying fortress of Tiamat, the Wind Fiend.',
    bossId: 42, // Tiamat
    encounterZone: 'SKY_CASTLE',
  },

  // ===== HIDDEN CAVES =====
  {
    id: 'WATERFALL_CAVE',
    name: 'Waterfall Cave',
    type: LocationType.CAVE,
    x: 43,
    y: 31,
    description: 'A secret cave behind a waterfall. Find the Robot here.',
    encounterZone: 'WATERFALL',
  },
  {
    id: 'TREASURE_CAVE',
    name: 'Treasure Cave',
    type: LocationType.CAVE,
    x: 40,
    y: 41,
    description: 'A cave filled with treasure chests.',
  },
  {
    id: 'CARDIA_ISLANDS',
    name: 'Cardia Islands',
    type: LocationType.SPECIAL,
    x: 60,
    y: 12,
    description: 'Remote islands in the northeast. Home to rare encounters.',
    encounterZone: 'CARDIA',
  },

  // ===== FINAL DUNGEON =====
  {
    id: 'TEMPLE_FIENDS_REVISITED',
    name: 'Temple of Fiends (Past)',
    type: LocationType.DUNGEON,
    x: 11,
    y: 9, // Same as original Temple
    description: 'Travel 2000 years into the past to face the Four Fiends and Chaos.',
    encounterZone: 'TEMPLE_PAST',
    requiredItem: 'ALL_CRYSTALS', // Pseudo-item representing all 4 crystals lit
  },
];

// ========== ENCOUNTER ZONES ==========

export const ENCOUNTER_ZONES: Record<string, EncounterZone> = {
  // Early game - Cornelia region
  CORNELIA_FIELDS: {
    id: 'CORNELIA_FIELDS',
    name: 'Cornelia Plains',
    minLevel: 1,
    maxLevel: 3,
    encounterRate: 8,
    enemyGroups: [
      [1], // Goblin
      [1, 1], // 2 Goblins
      [2], // Wolf
      [2, 2], // 2 Wolves
      [1, 2], // Goblin + Wolf
    ],
    weights: [30, 25, 20, 15, 10],
  },

  TEMPLE_FIENDS: {
    id: 'TEMPLE_FIENDS',
    name: 'Temple of Fiends',
    minLevel: 2,
    maxLevel: 4,
    encounterRate: 12,
    enemyGroups: [
      [3], // Zombie
      [3, 3], // 2 Zombies
      [4], // Skeleton
      [4, 4], // 2 Skeletons
      [3, 4], // Zombie + Skeleton
      [10], // Imp
    ],
    weights: [25, 20, 20, 15, 15, 5],
  },

  // Coastal region
  COAST: {
    id: 'COAST',
    name: 'Coastal Region',
    minLevel: 3,
    maxLevel: 6,
    encounterRate: 10,
    enemyGroups: [
      [5], // Pirate
      [5, 5], // 2 Pirates
      [6], // Shark
      [5, 6], // Pirate + Shark
      [7], // Spider
    ],
    weights: [30, 20, 20, 15, 15],
  },

  MARSH: {
    id: 'MARSH',
    name: 'Marsh Cave',
    minLevel: 4,
    maxLevel: 8,
    encounterRate: 15,
    enemyGroups: [
      [8], // Snake
      [8, 8], // 2 Snakes
      [9], // Scorpion
      [7, 8], // Spider + Snake
      [3, 3, 3], // 3 Zombies
    ],
    weights: [25, 20, 20, 20, 15],
  },

  // Mid game
  FOREST: {
    id: 'FOREST',
    name: 'Forest',
    minLevel: 5,
    maxLevel: 10,
    encounterRate: 12,
    enemyGroups: [
      [11], // Ogre
      [2, 2, 2], // 3 Wolves
      [7, 7], // 2 Spiders
      [13], // Troll
    ],
    weights: [30, 30, 25, 15],
  },

  EARTH_CAVE: {
    id: 'EARTH_CAVE',
    name: 'Earth Cave',
    minLevel: 10,
    maxLevel: 15,
    encounterRate: 15,
    enemyGroups: [
      [11, 11], // 2 Ogres
      [13], // Troll
      [14], // Vampire
      [15], // Mummy
      [23], // Gargoyle
    ],
    weights: [25, 25, 20, 15, 15],
  },

  VOLCANO: {
    id: 'VOLCANO',
    name: 'Gurgu Volcano',
    minLevel: 15,
    maxLevel: 20,
    encounterRate: 18,
    enemyGroups: [
      [12], // Giant
      [24], // Golem
      [19], // Manticore
      [20], // Chimera
      [27], // Red Dragon
    ],
    weights: [30, 25, 20, 15, 10],
  },

  ICE_CAVE: {
    id: 'ICE_CAVE',
    name: 'Ice Cave',
    minLevel: 15,
    maxLevel: 20,
    encounterRate: 15,
    enemyGroups: [
      [16], // Wraith
      [17], // Specter
      [18], // Mindflayer
      [26], // Blue Dragon
    ],
    weights: [35, 30, 20, 15],
  },

  // Late game
  SEA_SHRINE: {
    id: 'SEA_SHRINE',
    name: 'Sea Shrine',
    minLevel: 20,
    maxLevel: 25,
    encounterRate: 20,
    enemyGroups: [
      [21], // Medusa
      [22], // Cockatrice
      [31], // Death Knight
      [32], // Dark Wizard
      [28], // Green Dragon
    ],
    weights: [25, 25, 20, 15, 15],
  },

  MIRAGE_TOWER: {
    id: 'MIRAGE_TOWER',
    name: 'Mirage Tower',
    minLevel: 22,
    maxLevel: 28,
    encounterRate: 18,
    enemyGroups: [
      [30], // Iron Golem
      [34], // Phantom
      [35], // Death Eye
      [36], // Evil Eye
    ],
    weights: [35, 30, 20, 15],
  },

  SKY_CASTLE: {
    id: 'SKY_CASTLE',
    name: 'Sky Castle',
    minLevel: 25,
    maxLevel: 30,
    encounterRate: 20,
    enemyGroups: [
      [37], // Sorcerer
      [38], // Demon
      [33], // Warmech (rare!)
      [26, 27], // Blue + Red Dragon
    ],
    weights: [40, 35, 5, 20],
  },

  WATERFALL: {
    id: 'WATERFALL',
    name: 'Waterfall Cave',
    minLevel: 18,
    maxLevel: 22,
    encounterRate: 15,
    enemyGroups: [
      [30], // Iron Golem
      [25], // Dragon Zombie
      [29], // Tyrannosaur
    ],
    weights: [40, 35, 25],
  },

  CARDIA: {
    id: 'CARDIA',
    name: 'Cardia Islands',
    minLevel: 28,
    maxLevel: 35,
    encounterRate: 25,
    enemyGroups: [
      [33], // Warmech
      [38, 38], // 2 Demons
      [26, 27, 28], // All dragons
    ],
    weights: [10, 45, 45],
  },

  // Final dungeon
  TEMPLE_PAST: {
    id: 'TEMPLE_PAST',
    name: 'Temple of Fiends (Past)',
    minLevel: 30,
    maxLevel: 40,
    encounterRate: 22,
    enemyGroups: [
      [31, 32], // Death Knight + Dark Wizard
      [37, 37], // 2 Sorcerers
      [38, 34], // Demon + Phantom
      [35, 36], // Death Eye + Evil Eye
      [33], // Warmech (rare encounter)
    ],
    weights: [30, 30, 20, 15, 5],
  },

  // Ocean encounters (requires ship/canoe/airship)
  OCEAN: {
    id: 'OCEAN',
    name: 'Ocean',
    minLevel: 8,
    maxLevel: 15,
    encounterRate: 12,
    enemyGroups: [
      [6], // Shark
      [6, 6], // 2 Sharks
      [5, 5, 5], // 3 Pirates
      [6, 6, 6], // 3 Sharks
    ],
    weights: [35, 30, 20, 15],
  },

  DESERT: {
    id: 'DESERT',
    name: 'Desert',
    minLevel: 12,
    maxLevel: 18,
    encounterRate: 10,
    enemyGroups: [
      [9, 9], // 2 Scorpions
      [19], // Manticore
      [22], // Cockatrice
      [25], // Dragon Zombie
    ],
    weights: [40, 25, 20, 15],
  },
};

// ========== TERRAIN ENCOUNTER MAPPING ==========

// Map terrain types to encounter zones
export function getEncounterZoneForTerrain(
  terrain: TerrainType,
  x: number,
  y: number
): string | null {
  // Check if we're near a specific location first
  const nearLocation = getNearestLocation(x, y, 3);
  if (nearLocation?.encounterZone) {
    return nearLocation.encounterZone;
  }

  // Default encounter zones by terrain
  switch (terrain) {
    case TerrainType.GRASS:
      // Determine region based on position
      if (y < 25) return 'CORNELIA_FIELDS';
      if (x > 40) return 'FOREST';
      return 'CORNELIA_FIELDS';

    case TerrainType.FOREST:
      return 'FOREST';

    case TerrainType.SWAMP:
      return 'MARSH';

    case TerrainType.DESERT:
      return 'DESERT';

    case TerrainType.OCEAN:
      return 'OCEAN';

    default:
      return null; // No encounters on this terrain
  }
}

// ========== HELPER FUNCTIONS ==========

// Get location at specific coordinates
export function getLocationAt(x: number, y: number): Location | null {
  return LOCATIONS.find(loc => loc.x === x && loc.y === y) ?? null;
}

// Get nearest location within radius
export function getNearestLocation(
  x: number,
  y: number,
  maxRadius: number
): Location | null {
  let nearest: Location | null = null;
  let nearestDist = Infinity;

  for (const loc of LOCATIONS) {
    const dist = Math.sqrt((loc.x - x) ** 2 + (loc.y - y) ** 2);
    if (dist <= maxRadius && dist < nearestDist) {
      nearest = loc;
      nearestDist = dist;
    }
  }

  return nearest;
}

// Check if terrain is walkable
export function isTerrainWalkable(terrain: TerrainType): boolean {
  switch (terrain) {
    case TerrainType.GRASS:
    case TerrainType.FOREST:
    case TerrainType.DESERT:
    case TerrainType.SWAMP:
    case TerrainType.ROAD:
    case TerrainType.BRIDGE:
    case TerrainType.TOWN:
    case TerrainType.CASTLE:
    case TerrainType.CAVE:
    case TerrainType.SHRINE:
      return true;

    case TerrainType.OCEAN:
    case TerrainType.MOUNTAIN:
    case TerrainType.RIVER:
      return false; // Requires special items (ship, canoe, etc.)

    default:
      return false;
  }
}

// Get terrain at coordinates
export function getTerrainAt(x: number, y: number): TerrainType {
  if (x < 0 || x >= WORLD_MAP_WIDTH || y < 0 || y >= WORLD_MAP_HEIGHT) {
    return TerrainType.OCEAN;
  }
  const row = WORLD_MAP[y];
  const terrain = row?.[x];
  return terrain !== undefined ? terrain : TerrainType.OCEAN;
}

// Get all locations of a specific type
export function getLocationsByType(type: LocationType): Location[] {
  return LOCATIONS.filter(loc => loc.type === type);
}

// Check if player can enter location (has required item)
export function canEnterLocation(location: Location, inventory: string[]): boolean {
  if (!location.requiredItem) return true;
  return inventory.includes(location.requiredItem);
}
